import { useCallback } from 'react';
import { ElementUpdateData, EffectUpdateData } from '../types';
import { currentClip, saveClipChanges, updateClipMetadataWithId } from '@/stores/clip';
import { showErrorToast } from '@/libs/toast/toast';
import { getClonedPlayerConfigFromClip } from '@/player-config/src/utils/clipUtils';
import { ElementsTrack as ElementsTrackType } from '@/player-config/src/types/RemotionPlayerConfigTypes/Track/ElementsTrack';
import { Effect } from '@/player-config/src/types/RemotionPlayerConfigTypes/Animations/Effects';
import { TrackType } from '@/player-config/src/types/Track/Type';
import { SegmentType } from '@/player-config/src/types/Segment/Type';
import { deleteElementHandler } from '@/Pages/Clip/ClipPlayer/ClipAssets/handlers';

export const useAiElementUpdates = () => {
  const handleElementUpdate = useCallback((clipId: string, elementId: string, updates: ElementUpdateData) => {
    try {
      const clip = currentClip.getSnapshot()[clipId];
      const playerConfig = getClonedPlayerConfigFromClip(clip);

      if (!playerConfig) {
        throw new Error('Player config not found');
      }

      // Find the track that contains the element
      let elementFound = false;
      const updatedTracks = playerConfig.playerConfig.tracks.map((track: any) => {
        if (track.type !== TrackType.ELEMENTS) {
          return track;
        }

        const elementsTrack = track as ElementsTrackType;
        const elementIndex = elementsTrack.elements.findIndex(element => element.id === elementId);

        if (elementIndex !== -1) {
          elementFound = true;
          // Update the element using immutable approach
          const updatedElements = [...elementsTrack.elements];
          const elementWithUpdates = {
            ...updatedElements[elementIndex],
            ...updates
          } as any;

          updatedElements[elementIndex] = { ...elementWithUpdates, metadata: undefined };

          return {
            ...elementsTrack,
            elements: updatedElements
          };
        }

        return track;
      });

      if (!elementFound) {
        throw new Error(`Element not found in any track: ${elementId}`);
      }

      // Update the player config with the new tracks
      playerConfig.playerConfig.tracks = updatedTracks;

      // Update clip metadata and save changes
      updateClipMetadataWithId(clipId, { player_config: playerConfig as any });
      saveClipChanges({ clipId });
    } catch (error) {
      console.error('Error updating element:', error);
      showErrorToast('Something went wrong while updating element');
      throw error;
    }
  }, []);

  const handleElementDelete = useCallback((clipId: string, elementId: string) => {
    try {
      const clip = currentClip.getSnapshot()[clipId];
      if (!clip) {
        throw new Error('Clip not found');
      }

      deleteElementHandler(clip, clipId, elementId);
    } catch (error) {
      console.error('Error deleting element:', error);
      showErrorToast('Something went wrong while deleting element');
      throw error;
    }
  }, []);

  const handleElementMove = useCallback(
    (clipId: string, elementId: string, newFromFrame: number, newToFrame: number) => {
      handleElementUpdate(clipId, elementId, {
        fromFrame: newFromFrame,
        toFrame: newToFrame
      });
    },
    [handleElementUpdate]
  );

  const handleElementResize = useCallback(
    (clipId: string, elementId: string, newFromFrame: number, newToFrame: number) => {
      handleElementUpdate(clipId, elementId, {
        fromFrame: newFromFrame,
        toFrame: newToFrame
      });
    },
    [handleElementUpdate]
  );

  const handleEffectUpdate = useCallback((clipId: string, effectId: string, updates: EffectUpdateData) => {
    try {
      const clip = currentClip.getSnapshot()[clipId];
      const playerConfig = getClonedPlayerConfigFromClip(clip);

      if (!playerConfig) {
        throw new Error('Player config not found');
      }

      let effectFound = false;
      let hasActualChanges = false;
      const updatedTracks = playerConfig.playerConfig.tracks.map((track: any) => {
        // Check for effects in ELEMENTS tracks (effects within elements)
        if (track.type === TrackType.ELEMENTS) {
          const elementsTrack = track as ElementsTrackType;
          const updatedElements = elementsTrack.elements.map(element => {
            if (!(element as any).effects) {
              return element;
            }

            const effectIndex = (element as any).effects.findIndex((effect: Effect) => effect.id === effectId);
            if (effectIndex !== -1) {
              effectFound = true;
              // Update the effect using immutable approach
              const updatedEffects = [...(element as any).effects];
              const currentEffect = updatedEffects[effectIndex];
              const updatedEffect = {
                ...currentEffect,
                ...updates,
                // Handle params separately to merge them correctly
                ...(updates.params && {
                  params: {
                    ...currentEffect.params,
                    ...updates.params
                  }
                })
              };

              const hasChanges = JSON.stringify(currentEffect) !== JSON.stringify(updatedEffect);
              if (!hasChanges) {
                return element;
              }

              hasActualChanges = true;
              updatedEffects[effectIndex] = { ...updatedEffect, metadata: undefined };

              return {
                ...element,
                effects: updatedEffects
              };
            }

            return element;
          });

          return {
            ...elementsTrack,
            elements: updatedElements
          };
        }

        // Check for effects in SEGMENTS track (effects on composite segment)
        if (track.type === TrackType.SEGMENTS) {
          const segmentsTrack = track;
          const updatedSegments = segmentsTrack.segments.map(segment => {
            if (segment.type !== SegmentType.COMPOSITE || !segment.effects) {
              return segment;
            }

            const effectIndex = segment.effects.findIndex((effect: Effect) => effect.id === effectId);
            if (effectIndex !== -1) {
              effectFound = true;
              // Update the effect using immutable approach
              const updatedEffects = [...segment.effects];
              const currentEffect = updatedEffects[effectIndex];
              const updatedEffect = {
                ...currentEffect,
                ...updates,
                // Handle params separately to merge them correctly
                ...(updates.params && {
                  params: {
                    ...currentEffect.params,
                    ...updates.params
                  }
                })
              };

              const hasChanges = JSON.stringify(currentEffect) !== JSON.stringify(updatedEffect);
              if (!hasChanges) {
                return segment;
              }

              hasActualChanges = true;
              updatedEffects[effectIndex] = { ...updatedEffect, metadata: undefined };

              return {
                ...segment,
                effects: updatedEffects
              };
            }

            return segment;
          });

          return {
            ...segmentsTrack,
            segments: updatedSegments
          };
        }

        return track;
      });

      if (!effectFound) {
        throw new Error(`Effect not found in any element or composite segment: ${effectId}`);
      }

      if (hasActualChanges) {
        // Update the player config with the new tracks
        playerConfig.playerConfig.tracks = updatedTracks;

        // Update clip metadata and save changes
        updateClipMetadataWithId(clipId, { player_config: playerConfig as any });
        saveClipChanges({
          clipId,
          shouldGenerateThumbnail: false
        });
      }
    } catch (error) {
      console.error('Error updating effect:', error);
      showErrorToast('Something went wrong while updating effect');
      throw error;
    }
  }, []);

  const handleEffectDelete = useCallback((clipId: string, effectId: string) => {
    try {
      const clip = currentClip.getSnapshot()[clipId];
      const playerConfig = getClonedPlayerConfigFromClip(clip);

      if (!playerConfig) {
        throw new Error('Player config not found');
      }

      let effectFound = false;
      const updatedTracks = playerConfig.playerConfig.tracks.map((track: any) => {
        // Check for effects in ELEMENTS tracks (effects within elements)
        if (track.type === TrackType.ELEMENTS) {
          const elementsTrack = track as ElementsTrackType;
          const updatedElements = elementsTrack.elements.map(element => {
            if (!(element as any).effects) {
              return element;
            }

            const effectIndex = (element as any).effects.findIndex((effect: Effect) => effect.id === effectId);
            if (effectIndex !== -1) {
              effectFound = true;
              // Remove the effect using immutable approach
              const updatedEffects = (element as any).effects.filter((effect: Effect) => effect.id !== effectId);

              return {
                ...element,
                effects: updatedEffects
              };
            }

            return element;
          });

          return {
            ...elementsTrack,
            elements: updatedElements
          };
        }

        // Check for effects in SEGMENTS track (effects on composite segment)
        if (track.type === TrackType.SEGMENTS) {
          const segmentsTrack = track;
          const updatedSegments = segmentsTrack.segments.map(segment => {
            if (segment.type !== SegmentType.COMPOSITE || !segment.effects) {
              return segment;
            }

            const effectIndex = segment.effects.findIndex((effect: Effect) => effect.id === effectId);
            if (effectIndex !== -1) {
              effectFound = true;
              // Remove the effect using immutable approach
              const updatedEffects = segment.effects.filter((effect: Effect) => effect.id !== effectId);

              return {
                ...segment,
                effects: updatedEffects
              };
            }

            return segment;
          });

          return {
            ...segmentsTrack,
            segments: updatedSegments
          };
        }

        return track;
      });

      if (!effectFound) {
        throw new Error(`Effect not found in any element or composite segment: ${effectId}`);
      }

      // Update the player config with the new tracks
      playerConfig.playerConfig.tracks = updatedTracks;

      // Update clip metadata and save changes
      updateClipMetadataWithId(clipId, { player_config: playerConfig as any });
      saveClipChanges({ clipId });
    } catch (error) {
      console.error('Error deleting effect:', error);
      showErrorToast('Something went wrong while deleting effect');
      throw error;
    }
  }, []);

  const handleEffectMove = useCallback(
    (clipId: string, effectId: string, newStart: number) => {
      handleEffectUpdate(clipId, effectId, { start: newStart });
    },
    [handleEffectUpdate]
  );

  const handleEffectResize = useCallback(
    (clipId: string, effectId: string, newStart: number, newDuration: number) => {
      try {
        const clip = currentClip.getSnapshot()[clipId];
        const playerConfig = getClonedPlayerConfigFromClip(clip);

        if (!playerConfig) {
          throw new Error('Player config not found');
        }

        // First, find the current effect to get its original duration and animation durations
        let currentEffect: Effect | null = null;

        // Search for the effect to get current values
        for (const track of playerConfig.playerConfig.tracks) {
          if (track.type === TrackType.ELEMENTS) {
            const elementsTrack = track as ElementsTrackType;
            for (const element of elementsTrack.elements) {
              if ((element as any).effects) {
                const effect = (element as any).effects.find((e: Effect) => e.id === effectId);
                if (effect) {
                  currentEffect = effect;
                  break;
                }
              }
            }
            if (currentEffect) {
              break;
            }
          }

          if (track.type === TrackType.SEGMENTS) {
            const segmentsTrack = track;
            for (const segment of segmentsTrack.segments) {
              if (segment.type === 'COMPOSITE' && segment.effects) {
                const effect = segment.effects.find((e: Effect) => e.id === effectId);
                if (effect) {
                  currentEffect = effect;
                  break;
                }
              }
            }
            if (currentEffect) {
              break;
            }
          }
        }

        if (!currentEffect) {
          throw new Error(`Effect not found: ${effectId}`);
        }

        // Calculate the duration change ratio
        const originalDuration = currentEffect.duration;
        const durationRatio = newDuration / originalDuration;

        // Calculate new animation durations proportionally
        const newAnimationInDuration = currentEffect.animationInDuration
          ? currentEffect.animationInDuration * durationRatio
          : undefined;
        const newAnimationOutDuration = currentEffect.animationOutDuration
          ? currentEffect.animationOutDuration * durationRatio
          : undefined;

        // Update the effect with new values
        const updates: EffectUpdateData = {
          start: newStart,
          duration: newDuration,
          ...(newAnimationInDuration !== undefined && { animationInDuration: newAnimationInDuration }),
          ...(newAnimationOutDuration !== undefined && { animationOutDuration: newAnimationOutDuration })
        };

        handleEffectUpdate(clipId, effectId, updates);
      } catch (error) {
        console.error('Error resizing effect:', error);
        showErrorToast('Something went wrong while resizing effect');
        throw error;
      }
    },
    [handleEffectUpdate]
  );

  return {
    handleElementUpdate,
    handleElementDelete,
    handleElementMove,
    handleElementResize,
    handleEffectUpdate,
    handleEffectDelete,
    handleEffectMove,
    handleEffectResize
  };
};
